<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    :content-class="isShowFlow ? 'modal-full' : 'modal-medium'"
    title="サイト情報"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <v-row>
              <v-col class="d-flex flex-row ga-2 justify-end">
                <v-btn
                  v-if="isShowFlow"
                  v-permission.config
                  color="primary"
                  @click="modifyFlow"
                  >フロー変更</v-btn
                >
                <v-btn
                  v-permission.config
                  color="primary"
                  @click="modifySiteInfo"
                  >サイト変更</v-btn
                >
                <v-btn
                  v-permission.config
                  color="primary"
                  @click="deleteSiteInfo"
                  >削除</v-btn
                >
              </v-col>
            </v-row>
            <!-- 回線番号 -->
            <v-row>
              <v-col cols="12">
                <app-label-group label="回線番号" :value="siteData.enumber" />
              </v-col>
            </v-row>
            <!-- VLAN -->
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="VLAN"
                  :value="
                    siteData.vlanIdType === 'SPECIFIED'
                      ? siteData.vlanId
                      : $filter.enumTo(siteData.vlanIdType, 'vlanIdType')
                  "
                />
              </v-col>
            </v-row>
            <!-- サイト名 -->
            <v-row class="mb-3">
              <v-col cols="12">
                <app-label-group label="サイト名" :value="siteData.siteName" />
              </v-col>
            </v-row>

            <!-- フロー情報一覧 -->
            <div v-if="isShowFlow">
              <h3 class="mb-3">フロー情報一覧</h3>
              <div>
                <app-table
                  density="compact"
                  primary-key="flowSeq"
                  :fields="flow.fields"
                  :items="flow.items"
                  :per-page="-1"
                  :td-class="cellClass"
                  :tr-class="() => undefined"
                >
                  <!--接続先VNコネクト名（W番号）/ 接続先VPN（VPNコード）-->
                  <template #head(destWnumVpnCode)>
                    接続先VNコネクト名(W番号)/ <br />
                    接続先VPN(VPNコード)
                  </template>
                </app-table>
              </div>
            </div>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { SiteListEntity } from "@/views/nwSetting/site/SiteList.vue";
import SiteEdit from "@/modals/site/SiteEdit.vue";
import type { FlowResponse } from "@/apis/VirtualNetworkApi";
import type { VNConnect } from "@/apis/VNConnectApi";
import type {
  Type1PrivateSiteDelete,
  Type1PrivateSiteDeleteLine,
} from "@/apis/Type1SiteApi";
import { VueFinalModal } from "vue-final-modal";
import type { AppTableHeader } from "@/components/AppTable.vue";
import SiteFlowListModify from "@/modals/site/SiteFlowListModify.vue";

export type FlowEntity = FlowResponse["flowList"][0];

export default defineComponent({
  name: "SiteReference",
  props: {
    siteData: {
      type: Object as PropType<SiteListEntity>,
      required: true,
    },
    type1SiteZoneKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      isLoaded: false,
      flow: {
        fields: [
          {
            key: "destWnumVpnCode",
            width: "300px",
            formatter: (value: string, key: null, item: FlowEntity) => {
              if (item.wnumber) {
                return `${item.vnConnectName}(${item.wnumber})`;
              } else {
                return `L3VPN(${item.vpnCode})`;
              }
            },
          },
          {
            key: "srcAddress",
            title: "送信元アドレス",
            width: "170px",
            formatter: (value: string) => this.$filter.emptyTo(value, "any"),
          },
          {
            key: "dstAddress",
            title: "宛先アドレス",
            width: "170px",
            formatter: (value: string) => this.$filter.emptyTo(value, "any"),
          },
          {
            key: "description",
            title: "備考",
            formatter: (value: string) => this.$filter.emptyTo(value, "-"),
          },
          {
            key: "flowStatus",
            title: "有効/無効",
            width: "170px",
            formatter: (value: string) =>
              this.$filter.enumTo(value, "flowStatusItems"),
          },
          {
            key: "ifStatus",
            title: "IF状態",
            width: "200px",
            formatter: (value: string) =>
              this.$filter.enumTo(value, "cloudVn_ifStatus"),
          },
        ] as AppTableHeader<FlowEntity>[],
        items: [] as FlowEntity[],
      },
      flowKeyId: "",
      vnConnectList: [] as VNConnect[],
    };
  },
  computed: {
    /** true: フロー表示, false: 非表示 */
    isShowFlow(): boolean {
      // 特定の回線種別 + VNコネクトが存在する + VPN/VNがL3/L3VPNのみでL2が存在しない
      switch (this.siteData.accessType) {
        case "ETHERNET_L2":
        case "ETHERNET_L3":
        case "BROADBAND_ACCESS2_L2":
        case "BROADBAND_ACCESS2_L3":
        case "WIRELESS_ACCESS_L2":
        case "WIRELESS_ACCESS_L3":
        case "EXTEND_ETHERNET":
        case "IPSEC":
        case "PLATFORM_GATEWAY":
        case "PF_ACCESS_GW":
          break;
        default:
          return false;
      }

      const siteVnConnects = this.vnConnectList.filter(
        (e) =>
          e.siteName === this.siteData.siteName &&
          e.vlanIdType === this.siteData.vlanIdType &&
          e.vlanId === this.siteData.vlanId
      );
      return (
        siteVnConnects.some((e) => e.vnType !== "L2") &&
        !siteVnConnects.some((e) => e.vnType === "L2")
      );
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.vnConnectList = (
        await this.$api.vNConnect.getVNConnects()
      ).vnConnectList;

      if (this.isShowFlow) {
        const flowData = await this.$api.virtualNetwork.getFlowSettings({
          enumber: this.siteData.enumber,
          vlanIdType: this.siteData.vlanIdType,
          vlanId: this.siteData.vlanId,
        });
        this.flow.items = flowData.flowList;
        this.flowKeyId = flowData.flowKeyId;
      }
    },

    /** フロー変更 */
    async modifyFlow() {
      await this.$modal.show(SiteFlowListModify, {
        siteData: this.siteData,
        flowData: this.flow.items,
        flowKeyId: this.flowKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** サイト変更 */
    async modifySiteInfo() {
      await this.$modal.show(SiteEdit, {
        siteData: this.siteData,
        type1SiteZoneKeyId: this.type1SiteZoneKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** サイト削除 */
    async deleteSiteInfo() {
      /** 確認ダイアログの表示 */
      await this.$confirm(`サイトを削除します。よろしいですか？`);

      /** APIリクエストデータのマッピング処理 */
      const lineList = [] as Type1PrivateSiteDeleteLine[];
      if (lineList.find((i) => i.enumber === this.siteData.enumber)) {
        const idx = lineList.findIndex(
          (i) => i.enumber === this.siteData.enumber
        );
        lineList[idx].siteIdList.push(this.siteData.siteId);
      } else {
        lineList.push({
          enumber: this.siteData.enumber,
          siteIdList: [this.siteData.siteId],
        });
      }
      const deletedData: Type1PrivateSiteDelete = {
        lineList: lineList,
        type1SiteZoneKeyId: this.type1SiteZoneKeyId,
      };
      /** APIによるサイト削除処理 */
      await this.$api.type1SiteZone.postType1PrivateSiteDelete(deletedData);
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<FlowEntity>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
